"use client";

import Link from "next/link";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";

/**
 * Employer CTA on job detail pages (CM-379) — measures whether the block
 * added in 7edb781 actually drives anyone to /post-a-job, the same
 * impression+click pattern RelatedJobsRail already uses.
 *  - `employer_cta_shown` fires once, when the block scrolls into view (a
 *    real impression, so CTR is honest).
 *  - `employer_cta_clicked` fires on the "Post your job" click.
 * Tracking is consent-gated: no PostHog consent, no events, block still works.
 *
 * `placement` (98th-run handover's #1 next action, 2026-09-04 — NOT CM-396,
 * which is the separate hydration-crash task): 3 runs stable at 261 shown / 2
 * clicked (0.8% CTR) with the single "bottom" instance below the full job
 * description. `employer_cta_shown` only fires on a real IntersectionObserver
 * hit, so the impressions are genuine — visibility isn't the open question,
 * conversion-once-seen is. A byte-offset check of a real job page this run
 * found "bottom" sits at ~58% through the page's visible text, i.e. past a
 * full description read. Adding a compact "top" instance (rendered right
 * after the header, above the description) is the cheapest way to get
 * evidence on whether placement matters at all: same copy engine, same
 * event names, one new `placement` property so a future run can compare
 * top-CTR vs bottom-CTR instead of guessing.
 *
 * 101st run (2026-09-05): a full week post-split (188 top / 151 bottom
 * shown) still shows 0 clicks on EITHER placement — placement isn't the
 * lever. Both instances' actual click target was a plain underlined text
 * link (`text-brand-700 underline`) while every other CTA in this app
 * (JobAlertDialog, AlertNudge, HeaderCta, the /post-a-job page's own submit
 * button) uses a solid `bg-brand-600` button — this was the only
 * conversion surface in the codebase NOT styled as a button. Switched both
 * to the same solid-button pattern; same events/props, so a future run can
 * compare CTR before/after this change using the existing `placement` tag.
 */
export function EmployerCta({
  sourceJobId,
  priceLabel,
  listingDays,
  placement,
  compact = false,
}: {
  sourceJobId: number;
  priceLabel: string;
  listingDays: number;
  placement: "top" | "bottom";
  compact?: boolean;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const shown = useRef(false);

  useEffect(() => {
    if (shown.current) return;
    const el = ref.current;
    if (!el) return;
    const fire = () => {
      if (shown.current) return;
      shown.current = true;
      if (posthog.__loaded) {
        posthog.capture("employer_cta_shown", {
          source_job_id: sourceJobId,
          placement,
        });
      }
    };
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) fire();
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [sourceJobId, placement]);

  const onClick = () => {
    if (posthog.__loaded)
      posthog.capture("employer_cta_clicked", { source_job_id: sourceJobId, placement });
  };

  if (compact) {
    return (
      <aside
        ref={ref}
        className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-[13px] text-neutral-600"
        aria-label="For employers"
      >
        <span>
          Hiring for a role like this? Post it for {priceLabel}, live in minutes.
        </span>
        <Link
          href="/post-a-job"
          onClick={onClick}
          className="inline-flex h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-md bg-brand-600 px-3 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
        >
          Post your job →
        </Link>
      </aside>
    );
  }

  return (
    <aside
      ref={ref}
      className="mt-8 rounded-lg border border-neutral-200 bg-neutral-50 p-4"
      aria-label="For employers"
    >
      <p className="text-sm font-semibold text-neutral-900">
        Hiring for a role like this?
      </p>
      <p className="mt-1 text-sm text-neutral-600">
        Post it here for {priceLabel} — a highlighted listing for{" "}
        {listingDays} days, live within minutes. No account, no quote, no sales call.
      </p>
      <Link
        href="/post-a-job"
        onClick={onClick}
        className="mt-3 inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
      >
        Post your job →
      </Link>
    </aside>
  );
}
